import { useEffect, useRef } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

/**
 * Global scroll restoration component.
 * Drop this once inside <BrowserRouter> and it handles everything:
 *
 * - Saves scroll position to sessionStorage on every route change (leaving a page)
 * - On POP navigation (browser back/forward), restores the saved position
 * - On PUSH navigation (clicking a link), scrolls to top
 * - Handles async-rendered content with retry logic
 *
 * This replaces per-page scroll hacks entirely.
 */

const STORAGE_KEY_PREFIX = "scroll_";
const MAX_AGE_MS = 30 * 60 * 1000; // 30 minutes

function getKey(path: string) {
  return STORAGE_KEY_PREFIX + path;
}

function savePosition(path: string) {
  try {
    sessionStorage.setItem(
      getKey(path),
      JSON.stringify({ y: window.scrollY, ts: Date.now() }),
    );
  } catch {}
}

function loadPosition(path: string): number | null {
  try {
    const raw = sessionStorage.getItem(getKey(path));
    if (!raw) return null;
    const { y, ts } = JSON.parse(raw);
    if (Date.now() - ts > MAX_AGE_MS) {
      sessionStorage.removeItem(getKey(path));
      return null;
    }
    return y;
  } catch {
    return null;
  }
}

export default function ScrollRestore() {
  const { pathname } = useLocation();
  const navType = useNavigationType();
  const prevPath = useRef(pathname);

  useEffect(() => {
    // Save scroll position of the page we're LEAVING
    if (prevPath.current !== pathname) {
      savePosition(prevPath.current);
      prevPath.current = pathname;
    }

    if (navType === "POP") {
      // Browser back/forward — restore saved position
      const savedY = loadPosition(pathname);
      if (savedY != null && savedY > 0) {
        // Immediate attempt
        window.scrollTo(0, savedY);
        // Retry after content renders (async data)
        const retries = [50, 150, 300, 600];
        const timers = retries.map((delay) =>
          setTimeout(() => {
            if (Math.abs(window.scrollY - savedY) > 50) {
              window.scrollTo(0, savedY);
            }
          }, delay),
        );
        return () => timers.forEach(clearTimeout);
      }
    } else {
      // PUSH or REPLACE — scroll to top
      window.scrollTo(0, 0);
    }
  }, [pathname, navType]);

  // Also save on beforeunload (tab close, refresh)
  useEffect(() => {
    function handleUnload() {
      savePosition(pathname);
    }
    window.addEventListener("beforeunload", handleUnload);
    return () => window.removeEventListener("beforeunload", handleUnload);
  }, [pathname]);

  return null;
}
